import React, { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { Button } from 'src/components/Button';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { PERMISSIONS } from 'src/utils/permissions';
import NoResult from 'src/components/NoResult/NoResult';
import { useDeferredLoading } from 'src/hooks/useDeferredLoading';

import {
  AdminUserRecord,
  createAdminUser,
  fetchAdminUsers,
  fetchRoles,
  updateAdminUser,
} from 'src/api/adminManagement';
import { getAdminEmailDisplay, resolveRoleSelectValue } from 'src/api/adminUserUtils';
import { AdminRoleRecord } from 'src/utils/rbacCatalog';
import { AdminUserCreateFormPayload } from './components/AdminUserFormModal';

import Pagination from 'src/components/Pagination';
import AdminUserFormModal from './components/AdminUserFormModal';
import { MANAGEMENT_PAGE_SIZE } from './constants';
import { DataTable, DataTableBody, DataTableWrap, Hint, TableActions, TablePaginationWrap, ToolbarRow } from './styled';
import { ManagementTableSkeleton } from './ManagementTableSkeleton';

const AdminUsers: React.FC<{ onTotalChange?: Dispatch<SetStateAction<number | null>> }> = ({
  onTotalChange,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState<AdminUserRecord[]>([]);
  const [roles, setRoles] = useState<AdminRoleRecord[]>([]);
  const [formAdmin, setFormAdmin] = useState<AdminUserRecord | null | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetchAdminUsers(currentPage, MANAGEMENT_PAGE_SIZE),
        fetchRoles(),
      ]);
      setAdmins(usersRes.admins);
      setTotalPages(Math.max(1, usersRes.totalPages));
      onTotalChange?.(usersRes.totalCount);
      setRoles(rolesRes.roles);
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load users',
          isSuccess: false,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, onTotalChange]);

  useEffect(() => {
    load();
  }, [load]);

  const roleName = (admin: AdminUserRecord) =>
    admin.role?.name ??
    roles.find((r) => (r._id ?? r.key) === resolveRoleSelectValue(admin, roles))?.name ??
    admin.role_id ??
    '—';

  const notify = (message: string, isSuccess: boolean) => dispatch(setAppAlert({ message, isSuccess }));

  const handleCreate = async (payload: AdminUserCreateFormPayload) => {
    try {
      await createAdminUser(payload);
      notify('User created', true);
      await load();
    } catch (e: any) {
      const details = e?.response?.data?.details;
      const detailMsg =
        details && typeof details === 'object'
          ? Object.entries(details)
              .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(', ')}`)
              .join('; ')
          : '';
      notify(detailMsg || e?.response?.data?.error || 'Failed to create user', false);
      throw e;
    }
  };

  const handleUpdate = async (payload: { role_id: string; status: string }) => {
    if (!formAdmin?._id) return;
    try {
      await updateAdminUser(formAdmin._id, payload);
      notify('User updated', true);
      await load();
    } catch (e: any) {
      notify(e?.response?.data?.error ?? 'Failed to update user', false);
      throw e;
    }
  };

  const showLoading = useDeferredLoading(loading, admins.length > 0);

  return (
    <>
      <Hint>
        Create users with email, password and role. When editing, you can change role and account status.
      </Hint>
      <PermissionGuard permission={PERMISSIONS.ALL}>
        <ToolbarRow>
          <Button text="Create User" className="black" onClick={() => setFormAdmin(null)} />
        </ToolbarRow>
      </PermissionGuard>
      {admins.length > 0 || loading ? (
        <DataTableWrap>
          <DataTable>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Super admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <DataTableBody $dimmed={showLoading && admins.length > 0}>
              <ManagementTableSkeleton loading={loading} hasContent={admins.length > 0} columns={5} />
              {admins.map((admin) => (
                <tr key={admin._id}>
                  <td data-label="Email">{getAdminEmailDisplay(admin)}</td>
                  <td data-label="Role">{roleName(admin)}</td>
                  <td data-label="Status">{admin.status}</td>
                  <td data-label="Super admin">{admin.is_super_admin ? 'Yes' : 'No'}</td>
                  <td data-label="Actions">
                    <TableActions>
                      <PermissionGuard permission={PERMISSIONS.ALL}>
                        <Button text="Edit" className="outline small" onClick={() => setFormAdmin(admin)} />
                      </PermissionGuard>
                    </TableActions>
                  </td>
                </tr>
              ))}
            </DataTableBody>
          </DataTable>
          {totalPages > 1 && (
            <TablePaginationWrap>
              <Pagination
                nPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                urlKey="usersPage"
              />
            </TablePaginationWrap>
          )}
        </DataTableWrap>
      ) : (
        <NoResult />
      )}
      {formAdmin !== undefined && (
        <AdminUserFormModal
          admin={formAdmin}
          roles={roles}
          onClose={() => setFormAdmin(undefined)}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />
      )}
    </>
  );
};

export default AdminUsers;
